import React, { createContext, useContext, useState, useEffect } from "react";

const GlobalSettingsContext = createContext();

// Default structure for globalSettings
const defaultGlobalSettings = {
  result: [
    {
      invoiceSettings: {
        predefinedNo: {
          count: 0,
          prefix: "REC",
        },
      },
    },
  ],
};

// Function to validate and merge settings with default structure
const validateAndMergeSettings = (settings) => {
  if (!settings || typeof settings !== "object") {
    console.log("📝 Invalid settings object, using defaults");
    return defaultGlobalSettings;
  }

  const validated = {
    ...defaultGlobalSettings,
    ...settings,
  };

  // Ensure result exists
  if (!Array.isArray(validated.result) || validated.result.length === 0) {
    validated.result = defaultGlobalSettings.result;
  } else {
    validated.result[0] = {
      ...defaultGlobalSettings.result[0],
      ...validated.result[0],
    };

    if (!validated.result[0].invoiceSettings) {
      validated.result[0].invoiceSettings =
        defaultGlobalSettings.result[0].invoiceSettings;
    } else {
      validated.result[0].invoiceSettings.predefinedNo = {
        ...defaultGlobalSettings.result[0].invoiceSettings.predefinedNo,
        ...validated.result[0].invoiceSettings.predefinedNo,
      };
    }
  }

  return validated;
};

export const GlobalSettingsProvider = ({ children }) => {
  console.log("🚀 GlobalSettingsProvider initializing...");

  const [globalSettings, setGlobalSettingsState] = useState(() => {
    try {
      console.log("📦 Attempting to load settings from localStorage...");

      if (typeof Storage === "undefined") {
        console.warn("⚠️ localStorage is not available in this environment");
        return defaultGlobalSettings;
      }

      const stored = localStorage.getItem("globalSettings");
      console.log("📄 Raw localStorage value:", stored);

      if (
        stored === null ||
        stored === "undefined" ||
        stored === "null" ||
        stored === ""
      ) {
        console.log("✅ No valid stored settings found, using default structure");
        return defaultGlobalSettings;
      }

      const parsed = JSON.parse(stored);
      console.log("✅ Successfully parsed settings:", parsed);

      const validatedSettings = validateAndMergeSettings(parsed);
      console.log("✅ Validated settings:", validatedSettings);

      return validatedSettings;
    } catch (error) {
      console.error("❌ Error loading settings from localStorage:", error);
      try {
        localStorage.removeItem("globalSettings");
        console.log("🧹 Cleared corrupted localStorage data");
      } catch (clearError) {
        console.error("❌ Error clearing localStorage:", clearError);
      }
      return defaultGlobalSettings;
    }
  });

  console.log("🎯 Initial globalSettings state:", globalSettings);

  // Custom setter
  const setGlobalSettings = (newSettingsOrUpdater) => {
    console.log("🔄 setGlobalSettings called with:", newSettingsOrUpdater);

    try {
      if (typeof newSettingsOrUpdater === "function") {
        setGlobalSettingsState((prevSettings) => {
          const safePrevSettings = prevSettings || defaultGlobalSettings;
          const updatedSettings = newSettingsOrUpdater(safePrevSettings);

          const validatedSettings = validateAndMergeSettings(updatedSettings);

          if (validatedSettings) {
            localStorage.setItem(
              "globalSettings",
              JSON.stringify(validatedSettings)
            );
          }

          return validatedSettings;
        });
      } else {
        const validatedSettings = validateAndMergeSettings(newSettingsOrUpdater);

        setGlobalSettingsState(validatedSettings);

        if (validatedSettings) {
          localStorage.setItem(
            "globalSettings",
            JSON.stringify(validatedSettings)
          );
        } else {
          localStorage.removeItem("globalSettings");
        }
      }
    } catch (error) {
      console.error("❌ Error in setGlobalSettings:", error);
    }
  };

  // Debug effect to monitor state changes
  useEffect(() => {
    console.log("🔄 globalSettings state changed:", globalSettings);
    console.log(
      "🔄 Current localStorage value:",
      localStorage.getItem("globalSettings")
    );

    if (globalSettings) {
      console.log("🔍 Structure validation:");
      console.log("  - has result array:", Array.isArray(globalSettings.result));
      console.log("  - result length:", globalSettings.result?.length);
      console.log(
        "  - has invoiceSettings:",
        !!globalSettings.result?.[0]?.invoiceSettings
      );
      console.log(
        "  - has predefinedNo:",
        !!globalSettings.result?.[0]?.invoiceSettings?.predefinedNo
      );
      console.log(
        "  - current count:",
        globalSettings.result?.[0]?.invoiceSettings?.predefinedNo?.count
      );
      console.log(
        "  - current prefix:",
        globalSettings.result?.[0]?.invoiceSettings?.predefinedNo?.prefix
      );
    }
  }, [globalSettings]);

  // Initialize localStorage if it's empty
  useEffect(() => {
    const stored = localStorage.getItem("globalSettings");
    if (!stored || stored === "undefined" || stored === "null") {
      console.log("🔧 Initializing localStorage with default settings");
      localStorage.setItem("globalSettings", JSON.stringify(globalSettings));
    }
  }, []);

  console.log("🏭 Rendering GlobalSettingsProvider");

  return (
    <GlobalSettingsContext.Provider value={{ globalSettings, setGlobalSettings }}>
      {children}
    </GlobalSettingsContext.Provider>
  );
};

export const useGlobalSettings = () => {
  console.log("🪝 useGlobalSettings hook called");
  const context = useContext(GlobalSettingsContext);

  if (context === undefined) {
    throw new Error("useGlobalSettings must be used within a GlobalSettingsProvider");
  }

  return context;
};
